import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Reveal — blendet Kinder beim Scrollen in den sichtbaren Bereich sanft ein.
 * Nutzt framer-motion `whileInView`. Respektiert prefers-reduced-motion.
 *
 * Props:
 *  - direction: 'up' | 'down' | 'left' | 'right' | 'none'  (default: 'up')
 *  - delay:    ms-Wartezeit vor der Animation (default: 0)
 *  - duration: Animationsdauer in Sekunden (default: 0.6)
 *  - distance: Versatz in px (default: 24)
 *  - once:     nur einmal animieren (default: true)
 *  - as:       HTML/ motion-Element-Tag (default: 'div')
 *  - className, style, ...rest werden weitergereicht
 */
const offsetFor = (direction, distance) => {
  switch (direction) {
    case 'up':    return { y: distance };
    case 'down':  return { y: -distance };
    case 'left':  return { x: distance };
    case 'right': return { x: -distance };
    default:      return {};
  }
};

const Reveal = ({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.6,
  distance = 24,
  once = true,
  as = 'div',
  className,
  style,
  ...rest
}) => {
  const reduceMotion = useReducedMotion();
  const MotionTag = motion[as] || motion.div;

  // Bei bevorzugt reduzierter Bewegung: kein Versatz, nur sanftes Einblenden.
  const initial = reduceMotion
    ? { opacity: 0 }
    : { opacity: 0, ...offsetFor(direction, distance) };

  return (
    <MotionTag
      initial={initial}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, amount: 0.2 }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

export default Reveal;
